import { Formik } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { AsyncTypeahead } from 'react-bootstrap-typeahead'
import api from '../../../config/AxiosInstance'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'
import { BatchAddEditSchema } from '../../../utils/validationSchema'
import CustomButton from '../../Button/CustomButton'
import CustomCloseButton from '../../Button/CustomCloseButton'
import DatePickerComponent from '../../Input/DatePicker'
import TextField from '../../Input/TextInput'
import { convertDateWithTime } from '../../../helpers/constants'
import { FileText, X, CloudUpload } from 'lucide-react'

function ModalEditBatch({ show, onHide, data: batchId, setIsUpdate }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const refItem = useRef(AsyncTypeahead)
  const filterItem = () => true
  const [isLoadingItem, setIsLoadingItem] = useState(false)
  const [initialValues, setInitialValues] = useState(null)
  const [previewFile, setPreviewFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    if (show && batchId) {
      fetchBatch(batchId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, batchId])

  const minSelectableDate = new Date()
  minSelectableDate.setDate(minSelectableDate.getDate() + 1)

  async function fetchItems(query) {
    try {
      const res = await api.get(`master/items/all`, { params: { q: query } })
      const results = res.data
      setItems(results.data)
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  async function fetchBatch(id) {
    try {
      setLoading(true)
      const res = await api.get(`master/batch/${id}`)
      const result = res.data.data

      setInitialValues({
        item_id: result.item?.id || '',
        item_name: result.item?.item_name || '',
        batch: result.batch,
        manufacturing_date: result.manufacturing_date
          ? new Date(result.manufacturing_date)
          : '',
        exp_date: result.exp_date ? new Date(result.exp_date) : '',
        date_patch: result.date_patch ? new Date(result.date_patch) : '',
        note: result.note || '',
        coa: null
      })

      if (result.coa) {
        setPreviewFile({
          name: result.coa,
          isExisting: true
        })
      } else {
        setPreviewFile(null)
      }

      if (result.item) {
        setItems([result.item])
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
      onHide()
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (file, batchNumber) => {
    if (!batchNumber) {
      Common.showPopUpErrorMessage('Please enter batch number first')
      return
    }

    try {
      setIsUploading(true)
      const formData = new FormData()
      formData.append('batch', batchNumber.toUpperCase())
      formData.append('file', file)

      const response = await api.post(
        '/inbound/pr-draft/upload-coa',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )

      if (response.data.success) {
        setPreviewFile({ name: file.name, isExisting: false })
        Common.showPopUpSuccessMessage('COA uploaded successfully')
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message || 'Failed to upload COA'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async (values) => {
    try {
      setLoading(true)

      const payload = {
        item_id: values.item_id,
        batch: values.batch.toUpperCase(),
        manufacturing_date: convertDateWithTime(
          values.manufacturing_date,
          'YYYY-MM-DD'
        ),
        exp_date: convertDateWithTime(values.exp_date, 'YYYY-MM-DD'),
        note: values.note || ''
      }

      const response = await api.post(`/master/batch/${batchId}`, payload)

      const { data } = response
      if (data.success) {
        setIsUpdate((prev) => !prev)
        onHide()
        Common.showPopUpSuccessMessage(response?.data?.message)
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading(false)
    }
  }

  async function handleSearchItem(query) {
    try {
      setIsLoadingItem(true)
      if (query.length >= 3) {
        fetchItems(query)
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsLoadingItem(false)
    }
  }

  if (!initialValues) {
    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Batch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Formik
        initialValues={initialValues}
        validateOnChange={true}
        enableReinitialize={true}
        validationSchema={BatchAddEditSchema}
        onSubmit={handleSubmit}
      >
        {({
          handleBlur,
          handleSubmit,
          handleChange,
          setFieldValue,
          values,
          errors,
          touched,
          isValid,
          dirty,
          setFieldTouched
        }) => {
          return (
            <Form className="d-grid gap-2" onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>Update Batch</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row>
                  <Col md={12}>
                    <Form.Label
                      className="text-muted fw-bold text-uppercase"
                      style={{ fontSize: '13px', letterSpacing: '0.5px' }}
                    >
                      <small>ITEM</small>
                    </Form.Label>
                    <AsyncTypeahead
                      id="basic-typeahead-single"
                      labelKey="item_name"
                      isLoading={isLoadingItem}
                      filterBy={filterItem}
                      onSearch={handleSearchItem}
                      onChange={(e) => {
                        if (e.length) setFieldValue('item_id', e[0].id)
                      }}
                      defaultSelected={items.length > 0 ? items : []}
                      options={items}
                      placeholder="Choose a item ..."
                      ref={refItem}
                      isInvalid={errors.item_id}
                      disabled={true}
                      style={{ height: '45px' }}
                    />
                  </Col>
                  <Col md={6} className="mt-3">
                    <TextField
                      label="batch"
                      name="batch"
                      value={values.batch}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      placeholder="Enter Batch"
                      styles={{ height: '45px' }}
                      disabled={true}
                    />
                  </Col>
                  <DatePickerComponent
                    label="manufacturing date"
                    name="manufacturing_date"
                    value={values.manufacturing_date}
                    handleChange={setFieldValue}
                    handleBlur={() =>
                      setFieldTouched('manufacturing_date', true)
                    }
                    errors={errors}
                    touched={touched}
                    col={6}
                    required
                    styles={{ height: '45px' }}
                    disabled={true}
                  />
                  <DatePickerComponent
                    label="Exp. Date"
                    name="exp_date"
                    required
                    value={values.exp_date}
                    minDate={minSelectableDate}
                    handleChange={setFieldValue}
                    handleBlur={() => setFieldTouched('exp_date', true)}
                    errors={errors}
                    touched={touched}
                    col={6}
                    styles={{ height: '45px' }}
                    disabled={true}
                  />
                  <DatePickerComponent
                    label="date patch"
                    name="date_patch"
                    value={values.date_patch}
                    handleChange={setFieldValue}
                    handleBlur={() => setFieldTouched('date_patch', true)}
                    errors={errors}
                    touched={touched}
                    col={6}
                    required={false}
                    disabled={true}
                    styles={{ height: '45px' }}
                  />
                  <Col md={12} className="mt-1">
                    <TextField
                      label="note"
                      name="note"
                      value={values.note}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      placeholder="Enter Note"
                      as="textarea"
                      styles={{ height: '45px' }}
                      disabled={true}
                    />
                  </Col>

                  <Col md={12} className="mt-3">
                    <Form.Group>
                      <Form.Label
                        className="text-muted fw-bold text-uppercase"
                        style={{ fontSize: '13px', letterSpacing: '0.5px' }}
                      >
                        <small>COA {isUploading && '(Uploading...)'}</small>
                      </Form.Label>

                      {!previewFile ? (
                        <div
                          className={`custom-file-upload ${
                            isUploading ? 'opacity-50' : ''
                          }`}
                        >
                          <input
                            type="file"
                            disabled={isUploading}
                            onChange={(event) => {
                              const file = event.currentTarget.files[0]
                              if (file) {
                                handleFileUpload(file, values.batch)
                              }
                            }}
                            accept=".pdf"
                          />
                          <div className="upload-content">
                            <CloudUpload className="upload-icon" size={24} />
                            <span className="upload-text">
                              {isUploading ? 'Uploading...' : 'Upload File'}
                            </span>
                            <span className="upload-hint">PDF</span>
                          </div>
                        </div>
                      ) : (
                        <div className="file-preview-card d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <FileText
                              className="text-secondary me-2"
                              size={18}
                            />
                            <div
                              className="d-flex flex-column"
                              style={{ minWidth: 0 }}
                            >
                              <span
                                className="text-dark fw-bold text-truncate"
                                style={{
                                  maxWidth: '250px',
                                  fontSize: '0.9rem'
                                }}
                              >
                                {previewFile.name}
                              </span>
                              {previewFile.isExisting && (
                                <div
                                  className="text-muted"
                                  style={{ fontSize: '0.75rem', lineHeight: 1 }}
                                >
                                  Existing Document
                                </div>
                              )}
                            </div>
                          </div>
                          <div
                            onClick={() => {
                              setPreviewFile(null)
                              setFieldValue('coa', null)
                            }}
                            className="remove-file-btn"
                            title={
                              previewFile.isExisting
                                ? 'Remove current file'
                                : 'Remove selection'
                            }
                          >
                            <X size={16} />
                          </div>
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <CustomCloseButton text="Cancel" onClick={onHide} />
                <CustomButton
                  onClick={handleSubmit}
                  variant={!isValid || loading ? 'secondary' : 'warning'}
                  disabled={!isValid || loading}
                  text={!loading ? 'Update' : 'Loading...'}
                  classNameValue={`text-white ${
                    !isValid || loading ? 'opacity-25' : ''
                  }`}
                />
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default ModalEditBatch
